<template>
  <!--
    ── ¿Ha ido bien la llamada? ────────────────────────────────────────────
    Solo con el modo prueba (recorder.js) y solo para quien lo tiene. «Mal»
    abre lo que salió para corregirlo: lo corregido es lo que la llamada
    debería haber dado, y pasa al juego de pruebas tal cual.
  -->
  <div class="verdict">
    <template v-if="state === 'ask'">
      <p class="verdict__q">{{ t('assistant.test.question') }}</p>
      <div class="verdict__row">
        <button type="button" class="pill" :disabled="busy" @click="good">{{ t('assistant.test.good') }}</button>
        <button type="button" class="pill pill--bad" :disabled="busy" @click="openForm">{{ t('assistant.test.bad') }}</button>
      </div>
    </template>

    <form v-else-if="state === 'form'" class="verdict__form" @submit.prevent="bad">
      <p class="verdict__q">{{ t('assistant.test.expectedTitle') }}</p>
      <p class="verdict__help">{{ t('assistant.test.expectedHelp') }}</p>
      <p v-if="said.length" class="verdict__said">«{{ said.join(' · ') }}»</p>
      <label v-for="key in FIELDS" :key="key" class="verdict__field">
        <span>{{ t(`assistant.test.${key === 'question' ? 'expectedQuestion' : key}`) }}</span>
        <input v-model="expected[key]" type="text" maxlength="300">
      </label>
      <label class="verdict__field">
        <span>{{ t('assistant.test.comment') }}</span>
        <textarea v-model="comment" rows="2" maxlength="2000" />
      </label>
      <button type="submit" class="pill pill--bad" :disabled="busy">{{ t('assistant.test.save') }}</button>
    </form>

    <p v-else-if="state === 'saved'" class="verdict__done" role="status">{{ saved }}</p>
    <p v-if="problem" class="verdict__problem" role="alert">{{ problem }}</p>
  </div>
</template>

<script setup>
import { reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { api } from '@general/ui-kit'

const props = defineProps({ conversationId: { type: String, required: true } })
const { t } = useI18n()

const FIELDS = ['place', 'people', 'day', 'time', 'tasks', 'priority', 'customer', 'question', 'notes']
const state = ref('ask')
const busy = ref(false)
const problem = ref('')
const saved = ref('')
const said = ref([])
const comment = ref('')
const expected = reactive(Object.fromEntries(FIELDS.map(key => [key, ''])))

async function send(ok) {
  busy.value = true
  problem.value = ''
  try {
    await api.post(`/assistant/test-recording/${props.conversationId}/verdict`, ok
      ? { ok: true }
      : { ok: false, expected: { ...expected }, comment: comment.value })
    saved.value = ok ? t('assistant.test.savedGood') : t('assistant.test.savedBad')
    state.value = 'saved'
  } catch {
    problem.value = t('assistant.test.failed')
  } finally {
    busy.value = false
  }
}

const good = () => send(true)
const bad = () => send(false)

/** El formulario empieza con lo que salió: se corrige lo que está mal. */
async function openForm() {
  busy.value = true
  try {
    const data = await api.get(`/assistant/test-recording/${props.conversationId}`)
    said.value = data?.said ?? []
    for (const key of FIELDS) expected[key] = String(data?.got?.[key] ?? '')
    if (data?.question?.text) expected.question = data.question.text
  } catch {
    // Sin lo que salió, el formulario vacío sigue sirviendo.
  } finally {
    busy.value = false
    state.value = 'form'
  }
}
</script>

<style scoped>
.verdict { width: 100%; max-width: 420px; margin-top: 18px; display: flex; flex-direction: column; gap: 10px; text-align: left; }
.verdict__q { margin: 0; font-weight: 600; text-align: center; }
.verdict__row { display: flex; gap: 10px; justify-content: center; }
.verdict__help, .verdict__said { margin: 0; font-size: 13px; opacity: .75; }
.verdict__form { display: flex; flex-direction: column; gap: 8px; max-height: 46vh; overflow-y: auto; padding-right: 4px; }
.verdict__field { display: flex; flex-direction: column; gap: 3px; font-size: 12px; }
.verdict__field input, .verdict__field textarea {
  font: inherit; font-size: 14px; padding: 8px 10px; border-radius: 8px;
  border: 1px solid var(--color-border); background: var(--color-surface); color: var(--color-text);
}
.verdict__done { margin: 0; text-align: center; }
.verdict__problem { margin: 0; color: var(--color-danger); text-align: center; }
.pill--bad { border-color: var(--color-danger); color: var(--color-danger); }
</style>
